import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { fbAuth, fbError } from "../lib/firebase";
import { GoogleButton } from "./Login";
import { Helper } from "../components/ui-bits";
import { track } from "../lib/analytics";
import { toast } from "sonner";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", role_title: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { exchangeFirebase } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/projects";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const cred = await createUserWithEmailAndPassword(fbAuth, form.email.trim(), form.password);
      await updateProfile(cred.user, { displayName: form.name.trim() }).catch(() => {});
      sendEmailVerification(cred.user).catch(() => {});
      await exchangeFirebase(cred.user, {
        name: form.name, company: form.company, role_title: form.role_title,
      });
      track("signup_completed", { method: "password" });
      toast.success(`Verification email sent to ${form.email.trim()} — click the link when you're ready`);
      navigate(next);
    } catch (err) {
      setError(fbError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, opts = {}) => (
    <div>
      <label className="text-sm font-medium text-[#0B1F3A]">{label}</label>
      <input type={opts.type || "text"} required={!opts.optional} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={opts.placeholder || ""}
        className="mt-1 w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid={`signup-${key}-input`} />
      {opts.helper && <Helper>{opts.helper}</Helper>}
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <h1 className="font-serif text-3xl text-[#0B1F3A]">Create your free account</h1>
      <p className="text-sm text-[#475569] mt-1">Save projects, revisit roadmaps, and pick up right where you left off.</p>
      <form onSubmit={submit} className="space-y-3 mt-8">
        {field("name", "Full name")}
        {field("email", "Work email", { type: "email", helper: "We'll send a verification link — this is your login" })}
        {field("password", "Password", { type: "password", helper: "At least 6 characters" })}
        {field("company", "Company", { helper: "The organisation this project belongs to" })}
        {field("role_title", "Role / designation", { optional: true, helper: "e.g., Project Manager, EPC Head — optional" })}
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="signup-error">{error}</p>}
        <button type="submit" disabled={busy} className="w-full bg-[#1D4ED8] text-white rounded-md py-3 font-medium text-sm hover:bg-[#1638A8] disabled:opacity-60" data-testid="signup-submit-btn">
          {busy ? "Creating account…" : "Create free account"}
        </button>
      </form>
      <div className="mt-4"><GoogleButton next={next} /></div>
      <p className="text-sm text-[#475569] mt-6 text-center">
        Already registered? <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-[#0B1F3A] font-medium underline" data-testid="signup-login-link">Log in</Link>
      </p>
    </div>
  );
}
